import React, { useState, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  CircularProgress,
  Alert
} from "@mui/material";
import { useAuthContext } from "../../context/AuthProvider";
import { useNavigate } from "react-router-dom";
import { useManifiestos } from "../../hooks/useManifest";
import { useServiceOrders } from "../../hooks/administration/useServiceOrders"
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  History as HistoryIcon,
  Add as AddIcon,
  Public as PublicIcon,
  WhatsApp as WhatsAppIcon,
  TravelExplore as TravelExploreIcon 
} from "@mui/icons-material";
import ManifiestoForm from "./ManifestForm";
import RouteMap from "./rutas/RouteMap";
import ManifestOrderForm from "../operations/manifest/ManifestOrderForm";

// Configuración de colores para los estados
const ESTADO_COLOR_MAP = {
  CREADO: { label: "CREADO", color: "#90a4ae", text: "#fff" },
  PLANIFICADO: { label: "PLANIFICADO", color: "#ffb300", text: "#000" },
  EN_PROCESO: { label: "EN PROCESO", color: "#4caf50", text: "#fff" },
  COMPLETADO: { label: "COMPLETADO", color: "#1e88e5", text: "#fff" },
  CANCELADO: { label: "CANCELADO", color: "#e53935", text: "#fff" },
};

const TAB_CONFIG = [
  { label: "Creados", filter: (m) => m.manest === 'CREADO' },
  { label: "Planificados", filter: (m) => m.manest === 'PLANIFICADO' },
  { label: "En Proceso", filter: (m) => m.manest === 'EN_PROCESO' },
  { label: "Otros", filter: (m) => ['COMPLETADO', 'CANCELADO'].includes(m.manest) }
];

const ManifiestosDashboard = () => {
  const { user } = useAuthContext();
  const navigate = useNavigate();

  // Estados del componente
  const [openForm, setOpenForm] = useState(false);
  const [openFormV2, setOpenFormV2] = useState(false);
  const [editManifiesto, setEditManifiesto] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openConfirmWhatsapp, setOpenConfirmWhatsapp] = useState(false);
  const [openWhatsappResult, setOpenWhatsappResult] = useState(false);
  const [whatsappResult, setWhatsappResult] = useState(null);
  const [selectedManifiesto, setSelectedManifiesto] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
  const [openMap, setOpenMap] = useState(false);
  const [mapData, setMapData] = useState({ id: "", vehicleId: null });

  // Paginación y filtros
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [search, setSearch] = useState("");
  const [codigoFilter, setCodigoFilter] = useState("");
  const [currentTab, setCurrentTab] = useState(0);

  const { basicServiceOrders4Form } = useServiceOrders();

  // Hooks personalizados
  const {
    manifiestos,
    isLoading,
    error,
    deleteMutation,
    sendMessageWhatsappMutation
  } = useManifiestos();

  // IDs de roles
  const administradorID = Number(process.env.REACT_APP_ROL_ADMINISTRADOR_ID);
  const operacionesID = Number(process.env.REACT_APP_ROL_OPERACIONES_ID);
  const adminID = Number(process.env.REACT_APP_ROL_ADMIN_ID);

  // Mostrar alertas
  const showAlert = useCallback((message, severity = "success") => {
    setAlert({ open: true, message, severity });
    setTimeout(() => setAlert({ ...alert, open: false }), 5000);
  }, [alert]);

  // Formatear fecha larga
  const formatFechaLarga = useCallback((dateString) => {
    if (!dateString) return '';
    const fecha = new Date(dateString + 'T00:00:00');
    return new Intl.DateTimeFormat('es-PE', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    }).format(fecha);
  }, []);

  // Filtrar y ordenar manifiestos
  const displayedManifiestos = useMemo(() => {
    if (!manifiestos) return [];

    return manifiestos
      .filter(manifiesto => {
        const codigoCompleto = `MN-${String(manifiesto.manid).padStart(6, "0")}`.toLowerCase();
        const codigoRaw = String(manifiesto.manid);

        const matchesSearch = search === "" ||
          manifiesto.manori.toLowerCase().includes(search.toLowerCase()) ||
          manifiesto.mandes.toLowerCase().includes(search.toLowerCase());

        const matchesCodigo = codigoFilter === "" ||
          codigoCompleto.includes(codigoFilter.toLowerCase()) ||
          codigoRaw.includes(codigoFilter.replace(/^mn-0+/i, ""));

        return matchesSearch && matchesCodigo && TAB_CONFIG[currentTab].filter(manifiesto);
      })
      .sort((a, b) => new Date(b.manfec) - new Date(a.manfec));
  }, [manifiestos, search, codigoFilter, currentTab]);

  // Datos paginados
  const paginatedManifiestos = useMemo(() => (
    displayedManifiestos.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
  ), [displayedManifiestos, page, rowsPerPage]);

  // Handlers
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = useCallback(() => {
    if (!selectedManifiesto) return;

    deleteMutation.mutate((selectedManifiesto.manid), {
      onSuccess: () => {
        showAlert("Manifiesto eliminado con éxito");
        setOpenConfirm(false);
        setSelectedManifiesto(null);
      },
      onError: (error) => {
        showAlert(error.message || "Error al eliminar el manifiesto", "error");
        setOpenConfirm(false);
      },
    });
  }, [deleteMutation, selectedManifiesto, showAlert]);

  const handleSubmitWhatsapp = useCallback(() => {
    if (!selectedManifiesto) return;

    sendMessageWhatsappMutation.mutate(selectedManifiesto.manid, {
      onSuccess: (result) => {
        setWhatsappResult(result);
        setOpenWhatsappResult(true);
      },
      onError: (error) => {
        showAlert(error.message || "Error al enviar los mensajes", "error");
      },
      onSettled: () => {
        setOpenConfirmWhatsapp(false);
        setSelectedManifiesto(null);
      }
    });
  }, [selectedManifiesto, sendMessageWhatsappMutation, showAlert]);

  const handleCloseResultModal = () => {
    setOpenWhatsappResult(false);
    setWhatsappResult(null);
  };

  const getTabStyle = (tabIndex) => ({
    backgroundColor: tabIndex === currentTab ?
      `rgba(${parseInt(ESTADO_COLOR_MAP[TAB_CONFIG[tabIndex].filter.toString().split("'")[1]]?.color.slice(1, 3), 16)}, 
       ${parseInt(ESTADO_COLOR_MAP[TAB_CONFIG[tabIndex].filter.toString().split("'")[1]]?.color.slice(3, 5), 16)}, 
       ${parseInt(ESTADO_COLOR_MAP[TAB_CONFIG[tabIndex].filter.toString().split("'")[1]]?.color.slice(5, 7), 16)}, 0.3)` :
      'transparent',
    borderBottom: tabIndex === currentTab ?
      `3px solid ${ESTADO_COLOR_MAP[TAB_CONFIG[tabIndex].filter.toString().split("'")[1]]?.color}` :
      'none'
  });

  if (isLoading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
      <CircularProgress />
    </Box>
  );

  if (error) return (
    <Alert severity="error" sx={{ my: 2 }}>
      Error al cargar manifiestos: {error.message}
    </Alert>
  );

  return (
    <Paper sx={{ p: 2, position: 'relative' }}>
      {alert.open && (
        <Alert
          severity={alert.severity}
          sx={{
            position: 'fixed',
            top: 16,
            right: 16,
            zIndex: 9999,
            minWidth: 300
          }}
          onClose={() => setAlert({ ...alert, open: false })}
        >
          {alert.message}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" fontWeight="bold">Gestión de Manifiestos</Typography>
        <Box display="flex" gap={1}>
          {/*
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditManifiesto(null);
              setOpenForm(true);
            }}
            sx={{ textTransform: 'none' }}
          >
            Nuevo Manifiesto
          </Button>
          */}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditManifiesto(null);
              setOpenFormV2(true);
            }}
            sx={{ textTransform: 'none' }}
          >
            Nuevo Manifiesto
          </Button>
        </Box>
      </Box>

      {/* Filtros */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <TextField
          label="Buscar por origen/destino"
          variant="outlined"
          fullWidth
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 2, minWidth: 250 }}
        />
        <TextField
          label="Buscar por código (MN-000123 o 123)"
          variant="outlined"
          fullWidth
          size="small"
          value={codigoFilter}
          onChange={(e) => setCodigoFilter(e.target.value)}
          sx={{ flex: 1, minWidth: 250 }}
        />
      </Box>

      {/* Pestañas */}
      <Tabs
        value={currentTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{
          mb: 3,
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTab-root': {
            minHeight: 48,
            fontWeight: 600,
            fontSize: '0.9rem',
            color: 'text.secondary',
            '&.Mui-selected': {
              color: "#000",
            },
          },
        }}
      >
        {TAB_CONFIG.map((tab, index) => (
          <Tab
            key={index}
            label={tab.label}
            sx={getTabStyle(index)}
          />
        ))}
      </Tabs>

      {/* Tabla de resultados */}
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0' }}>
        <Table>
          <TableHead sx={{ bgcolor: 'background.default' }}>
            <TableRow>
              <TableCell><strong>Código</strong></TableCell>
              <TableCell><strong>Orden</strong></TableCell>
              <TableCell><strong>Descripción</strong></TableCell>
              <TableCell><strong>Fecha</strong></TableCell>
              <TableCell><strong>Origen</strong></TableCell>
              <TableCell><strong>Destino</strong></TableCell>
              <TableCell><strong>Estado</strong></TableCell>
              <TableCell align="center"><strong>Acciones</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedManifiestos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    No se encontraron manifiestos para esta selección
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedManifiestos.map((manifiesto) => (
                <TableRow
                  key={manifiesto.manid}
                  hover
                  sx={{ '&:last-child td': { borderBottom: 0 } }}
                >
                  <TableCell>
                    <Typography fontWeight="bold" color="primary">
                      MN-{String(manifiesto.manid).padStart(6, "0")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="bold">
                      OS-{String(manifiesto.ordsercabid).padStart(6, "0")}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 300 }}>
                    <Typography noWrap title={manifiesto.maninf}>
                      {manifiesto.maninf}
                    </Typography>
                  </TableCell>
                  <TableCell>{formatFechaLarga(manifiesto.manfec)}</TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography noWrap title={manifiesto.manori}>
                      {manifiesto.manori}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography noWrap title={manifiesto.mandes}>
                      {manifiesto.mandes}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ESTADO_COLOR_MAP[manifiesto.manest]?.label || manifiesto.manest}
                      sx={{
                        backgroundColor: ESTADO_COLOR_MAP[manifiesto.manest]?.color || '#ccc',
                        color: ESTADO_COLOR_MAP[manifiesto.manest]?.text || '#000',
                        fontWeight: 'bold',
                        minWidth: 100
                      }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 180 }}>
                    <Box display="flex" justifyContent="center" gap={1}>
                      {(user?.RolID === administradorID || user?.RolID === operacionesID) && (
                        <Tooltip title="Editar">
                          <IconButton
                            onClick={() => {
                              setEditManifiesto(manifiesto);
                              setOpenFormV2(true);
                            }}
                            size="small"
                          >
                            <EditIcon color="primary" fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title="Ver detalles">
                        <IconButton
                          onClick={() => navigate(`/administracion/manifiestos/${manifiesto.manid}/pasajeros`)}
                          size="small"
                        >
                          <VisibilityIcon sx={{ color: "#009688" }} fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Ver mapa de ruta">
                        <IconButton
                          onClick={() => {
                            setMapData({
                              id: manifiesto.manid,
                              vehicleId: manifiesto.vehid
                            });
                            setOpenMap(true);
                          }}
                          size="small"
                        >
                          <PublicIcon color="primary" fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      {[adminID, administradorID, operacionesID].includes(user?.RolID) && (
                      <Tooltip title="Monitor de Viaje">
                        <IconButton
                          onClick={() => navigate(`/administracion/manifiestos/${manifiesto.manid}/stream`, {
                            state: { vehid: manifiesto.vehid }
                          })}
                          size="small"
                        >
                          <TravelExploreIcon sx={{ color: "#009688" }} fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      )}

                      {user?.RolID === adminID && (
                        <>
                          <Tooltip title="Ver historial">
                            <IconButton
                              onClick={() => navigate(`/administracion/manifiestos/${manifiesto.manid}/historial`)}
                              size="small"
                            >
                              <HistoryIcon color="secondary" fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title={manifiesto.whatsappsent ? "Mensaje ya enviado" : "Enviar WhatsApp"}>
                            <span>
                              <IconButton
                                onClick={() => {
                                  setSelectedManifiesto(manifiesto);
                                  setOpenConfirmWhatsapp(true);
                                }}
                                disabled={manifiesto.whatsappsent}
                                color={manifiesto.whatsappsent ? "default" : "success"}
                                size="small"
                              >
                                <WhatsAppIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip title="Eliminar">
                            <IconButton
                              onClick={() => {
                                setSelectedManifiesto(manifiesto);
                                setOpenConfirm(true);
                              }}
                              size="small"
                            >
                              <DeleteIcon color="error" fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginación */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={displayedManifiestos.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ mt: 2 }}
      />

      {/* Modales */}
      <ManifiestoForm
        open={openForm}
        handleClose={() => setOpenForm(false)}
        defaultValues={editManifiesto}
      />

      <ManifestOrderForm
        open={openFormV2}
        handleClose={() => setOpenFormV2(false)}
        defaultValues={editManifiesto}
        orders={basicServiceOrders4Form}
      />

      {/* Modal de confirmación de eliminación */}
      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de eliminar el manifiesto {selectedManifiesto &&
              `MN-${String(selectedManifiesto.manid).padStart(6, "0")}`}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirm(false)}>Cancelar</Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleteMutation.isLoading}
          >
            {deleteMutation.isLoading ? <CircularProgress size={24} /> : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmación de WhatsApp */}
      <Dialog open={openConfirmWhatsapp} onClose={() => setOpenConfirmWhatsapp(false)}>
        <DialogTitle>
          Enviar notificación por WhatsApp
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se enviará un mensaje a todos los pasajeros del manifiesto{" "}
            {selectedManifiesto && `MN-${String(selectedManifiesto.manid).padStart(6, "0")}`}.
            ¿Desea continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenConfirmWhatsapp(false)}>Cancelar</Button>
          <Button
            onClick={handleSubmitWhatsapp}
            variant="contained"
            color="success"
            disabled={sendMessageWhatsappMutation.isLoading}
            startIcon={sendMessageWhatsappMutation.isLoading ? <CircularProgress size={20} /> : null}
          >
            {sendMessageWhatsappMutation.isLoading ? "Enviando..." : "Confirmar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de resultados de WhatsApp */}
      <Dialog
        open={openWhatsappResult}
        onClose={handleCloseResultModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Resultado del envío</DialogTitle>
        <DialogContent dividers>
          {whatsappResult && (
            <Box>
              <Box display="flex" justifyContent="space-between" mb={2}>
                <Typography>
                  <strong>Enviados:</strong> {whatsappResult.enviados}
                </Typography>
                <Typography color="error">
                  <strong>Fallidos:</strong> {whatsappResult.omitidos}
                </Typography>
              </Box>

              {whatsappResult.omitidos > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" color="error" gutterBottom>
                    Detalle de errores:
                  </Typography>
                  <Box maxHeight={300} overflow="auto">
                    <List dense>
                      {whatsappResult.pasajerosFail.map((fail, index) => (
                        <ListItem key={index} disablePadding>
                          <ListItemText
                            primary={`${index + 1}. ${fail.name || 'Sin nombre'} (${fail.number || 'Sin número'})`}
                            secondary={fail.reason?.error?.error_data?.details || 'Error desconocido'}
                            secondaryTypographyProps={{ color: 'error' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResultModal} variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal del mapa */}
      <Dialog
        open={openMap}
        onClose={() => setOpenMap(false)}
        fullWidth
        maxWidth="lg"
        sx={{ '& .MuiDialog-paper': { height: '80vh' } }}
      >
        <DialogTitle>
          Mapa de ruta - MN-{String(mapData.id).padStart(6, "0")}
        </DialogTitle>
        <DialogContent dividers>
          <RouteMap route={mapData.id} vehid={mapData.vehicleId} />
        </DialogContent>
      </Dialog>
    </Paper>
  );
};

export default ManifiestosDashboard;